import './Read.scss';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Navbar from "../../components/Navbar/Navbar";
import LoginPop from "../../components/LoginPop/LoginPop";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';
import { formatDate, sameDay } from '../../utils/dates';
import { selectionOffsets, applyHighlights, markFor } from '../../utils/highlights';

const EXTENSIONS = [StarterKit, Underline];

function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

function renderContent(raw) {
    if (!raw) return '';
    let doc;
    if (typeof raw === 'object') doc = raw;
    else {
        try { doc = JSON.parse(raw); }
        catch { return raw.replace(/\n/g, '<br>'); }
    }
    try { return generateHTML(doc, EXTENSIONS); } catch { return ''; }
}

function PanelIcon({ collapsed }) {
    // A sidebar panel with a chevron inside. The chevron points outward when
    // expanded (= "collapse me") and inward when collapsed (= "expand me").
    // The visual is direction-stable; works identically in LTR and RTL.
    const chevron = collapsed ? '8 8 12 12 8 16' : '12 8 8 12 12 16';
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
             stroke="currentColor" strokeWidth="1.7"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <line x1="15" y1="4" x2="15" y2="20" />
            <polyline points={chevron} />
        </svg>
    );
}

function Stars({ value, onSelect, interactive, size = 22 }) {
    const [hover, setHover] = useState(null);
    const display = hover ?? value ?? 0;
    return (
        <div className="stars" role={interactive ? 'radiogroup' : undefined}>
            {[1, 2, 3, 4, 5].map(n => {
                const active = display >= n;
                if (!interactive) {
                    return (
                        <svg key={n} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
                            <path
                                fill={active ? '#DAA520' : '#ddd'}
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                        </svg>
                    );
                }
                return (
                    <button
                        key={n}
                        type="button"
                        className="stars-btn"
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onSelect?.(n)}
                        aria-label={`${n} نجوم`}
                    >
                        <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
                            <path
                                fill={active ? '#DAA520' : '#ddd'}
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                        </svg>
                    </button>
                );
            })}
        </div>
    );
}

export default function Read() {

    const { id } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { user, authTokens } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);

    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const [chaptersHidden, setChaptersHidden] = useState(false);
    // Phones: the inline chapter list is hidden and a tab on the screen edge
    // opens this drawer instead, so the reader never scrolls to find chapters.
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [bookmarking, setBookmarking] = useState(false);
    // Logged-out readers who try to rate get an account prompt, not a toast.
    const [accountPrompt, setAccountPrompt] = useState(false);
    const [showLogin, setShowLogin] = useState(false);

    // Highlights: the reader's for this book, the popover that offers to save
    // or delete one, and the highlight to scroll to once its chapter renders.
    const [highlights, setHighlights] = useState([]);
    const [popover, setPopover] = useState(null);   // {x, y, pending} | {x, y, id}
    const [jumpTo, setJumpTo] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/books/library/book/?id=${encodeURIComponent(id)}`, {
                    headers: authHeaders(authTokens),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (cancelled) return;
                setBook(data.book);
                // /read/:id?chapter=<id> opens straight at a bookmarked chapter
                const want = Number(params.get('chapter'));
                const idx = (data.book?.chapters ?? []).findIndex(c => c.id === want);
                if (idx >= 0) setActiveIdx(idx);
                if (params.get('hl')) setJumpTo(Number(params.get('hl')));
            } catch (err) {
                if (!cancelled) {
                    setError('تعذر تحميل الكتاب');
                    setSnackbar({ content: 'تعذر تحميل الكتاب', success: false });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, authTokens, setSnackbar]);

    useEffect(() => {
        if (!user) { setHighlights([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/books/highlights/?book=${encodeURIComponent(id)}`, {
                    headers: authHeaders(authTokens),
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!cancelled) setHighlights(data.highlights ?? []);
            } catch { /* the chapter still reads fine without them */ }
        })();
        return () => { cancelled = true; };
    }, [id, user, authTokens]);

    const chapters = book?.chapters ?? [];
    const activeChapter = chapters[activeIdx];
    const activeHtml = useMemo(
        () => activeChapter ? renderContent(activeChapter.content) : '',
        [activeChapter]
    );
    const chapterHighlights = useMemo(
        () => highlights.filter(h => h.chapter === activeChapter?.id),
        [highlights, activeChapter]
    );
    const chapterBookmarked = !!activeChapter && (book?.bookmarked_chapters ?? []).includes(activeChapter.id);
    const isAuthor = !!user && !!book && user.username === book.author;

    // Paint the marks. React only rewrites innerHTML when activeHtml changes,
    // so reset it here before re-wrapping — otherwise marks would stack up.
    useEffect(() => {
        const root = contentRef.current;
        if (!root) return;
        root.innerHTML = activeHtml;
        applyHighlights(root, chapterHighlights);
        if (jumpTo != null) {
            const m = markFor(root, jumpTo);
            if (m) {
                m.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setJumpTo(null);
            }
        }
    }, [activeHtml, chapterHighlights, jumpTo]);

    // Offer to highlight whatever the reader selects inside the chapter text.
    useEffect(() => {
        if (!user || !activeChapter) return;
        let timer;
        const onChange = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const root = contentRef.current;
                if (!root) return;
                const sel = selectionOffsets(root);
                if (!sel) {
                    setPopover(p => (p?.pending ? null : p));
                    return;
                }
                setPopover({
                    x: sel.rect.left + sel.rect.width / 2 + window.scrollX,
                    y: sel.rect.top + window.scrollY,
                    pending: { start: sel.start, end: sel.end, text: sel.text },
                });
            }, 150);
        };
        document.addEventListener('selectionchange', onChange);
        return () => { clearTimeout(timer); document.removeEventListener('selectionchange', onChange); };
    }, [user, activeChapter]);

    const onContentClick = (e) => {
        const mark = e.target.closest?.('mark.read-hl');
        if (!mark) return;
        const rect = mark.getBoundingClientRect();
        setPopover({
            x: rect.left + rect.width / 2 + window.scrollX,
            y: rect.top + window.scrollY,
            id: Number(mark.dataset.hlId),
        });
    };

    const saveHighlight = async () => {
        const p = popover?.pending;
        if (!p) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/highlights/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ book: book.id, chapter: activeChapter.id, ...p }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const hl = await r.json();
            setHighlights(hs => [...hs, hl]);
            window.getSelection()?.removeAllRanges();
            setPopover(null);
        } catch {
            setSnackbar({ content: 'تعذر حفظ التظليل', success: false });
        }
    };

    const removeHighlight = useCallback(async (hlId) => {
        try {
            await fetch(`${API_BASE_URL}/api/books/highlights/?id=${hlId}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            setHighlights(hs => hs.filter(h => h.id !== hlId));
            setPopover(null);
        } catch {
            setSnackbar({ content: 'تعذر حذف التظليل', success: false });
        }
    }, [authTokens, setSnackbar]);

    const goToHighlight = (hl) => {
        const idx = chapters.findIndex(c => c.id === hl.chapter);
        if (idx < 0) return;
        setActiveIdx(idx);
        setJumpTo(hl.id);
    };

    const chapterList = (onPick) => (
        <ol className='read-toc-list'>
            {chapters.map((c, i) => (
                <li key={c.id} className={i === activeIdx ? 'is-active' : ''}>
                    <button onClick={() => onPick(i)}>
                        <span className='read-toc-num'>{i + 1}</span>
                        <span>{c.title || `الفصل ${i + 1}`}</span>
                        {(book?.bookmarked_chapters ?? []).includes(c.id) && (
                            <span className='read-toc-flag' title='علامة محفوظة'>★</span>
                        )}
                    </button>
                </li>
            ))}
        </ol>
    );

    const pickFromDrawer = (i) => {
        setActiveIdx(i);
        setDrawerOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const rate = async (value) => {
        if (!user) {
            setAccountPrompt(true);
            return;
        }
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/rate/`, {
                method: 'POST',
                headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: book.id, value }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
            setBook(data);
            setSnackbar({ content: 'شكرًا على تقييمك', success: true });
        } catch (err) {
            setSnackbar({ content: err.message?.startsWith('HTTP') ? 'تعذر إرسال التقييم' : err.message, success: false });
        }
    };

    const removeRating = async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/rate/?id=${book.id}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const updated = await r.json();
            setBook(updated);
            setSnackbar({ content: 'تم حذف تقييمك', success: true });
        } catch {
            setSnackbar({ content: 'تعذر حذف التقييم', success: false });
        }
    };

    const toggleBookmark = async () => {
        if (!user) {
            setSnackbar({ content: 'سجّل الدخول لحفظ العلامات المرجعية', success: false });
            return;
        }
        setBookmarking(true);
        try {
            if (book.is_bookmarked) {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/?book=${book.id}`, {
                    method: 'DELETE',
                    headers: authHeaders(authTokens),
                });
                setBook(b => ({ ...b, is_bookmarked: false }));
                setSnackbar({ content: 'تم حذف العلامة', success: true });
            } else {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/`, {
                    method: 'POST',
                    headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ book: book.id }),
                });
                setBook(b => ({ ...b, is_bookmarked: true }));
                setSnackbar({ content: 'تم حفظ العلامة', success: true });
            }
        } catch {
            setSnackbar({ content: 'تعذر تحديث العلامة', success: false });
        } finally {
            setBookmarking(false);
        }
    };

    const toggleChapterBookmark = async () => {
        if (!user) {
            setSnackbar({ content: 'سجّل الدخول لحفظ العلامات المرجعية', success: false });
            return;
        }
        if (!activeChapter) return;
        setBookmarking(true);
        const name = activeChapter.title || `الفصل ${activeIdx + 1}`;
        try {
            if (chapterBookmarked) {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/?book=${book.id}&chapter=${activeChapter.id}`, {
                    method: 'DELETE',
                    headers: authHeaders(authTokens),
                });
                setBook(b => ({ ...b, bookmarked_chapters: b.bookmarked_chapters.filter(c => c !== activeChapter.id) }));
                setSnackbar({ content: `تم حذف العلامة عند: ${name}`, success: true });
            } else {
                await fetch(`${API_BASE_URL}/api/books/bookmarks/`, {
                    method: 'POST',
                    headers: { ...authHeaders(authTokens), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ book: book.id, chapter: activeChapter.id }),
                });
                setBook(b => ({ ...b, bookmarked_chapters: [...(b.bookmarked_chapters ?? []), activeChapter.id] }));
                setSnackbar({ content: `تم حفظ علامة عند: ${name}`, success: true });
            }
        } catch {
            setSnackbar({ content: 'تعذر تحديث العلامة', success: false });
        } finally {
            setBookmarking(false);
        }
    };

    return (
        <>
        <Navbar />
        {showLogin && <LoginPop toggleShowLogin={() => setShowLogin(false)} />}
        {accountPrompt && !showLogin && (
            <div className='read-prompt' onClick={() => setAccountPrompt(false)}>
                <div className='read-prompt-box' role='dialog' aria-modal='true' onClick={e => e.stopPropagation()}>
                    <h3>أنشئ حسابًا لتقييم الكتاب</h3>
                    <p>التقييم متاح لأعضاء راوية. إنشاء الحساب مجاني ويستغرق دقيقة، وبه تحفظ علاماتك وتظليلاتك أيضًا.</p>
                    <div className='read-prompt-actions'>
                        <button type='button' className='read-prompt-primary' onClick={() => navigate('/register')}>
                            إنشاء حساب
                        </button>
                        <button type='button' className='read-prompt-ghost' onClick={() => { setAccountPrompt(false); setShowLogin(true); }}>
                            لديّ حساب — تسجيل الدخول
                        </button>
                    </div>
                    <button type='button' className='read-prompt-close' onClick={() => setAccountPrompt(false)} aria-label='إغلاق'>×</button>
                </div>
            </div>
        )}
        <main className='read-page'>
            <div className='read-container'>
                <div className='read-toolbar'>
                    <button className='read-back-btn' onClick={() => navigate('/library')}>
                        العودة للمكتبة
                    </button>
                    <div className='read-toolbar-actions'>
                        {chapters.length > 1 && chaptersHidden && (
                            <button
                                type='button'
                                className='read-toolbar-toggle read-toolbar-toc'
                                onClick={() => setChaptersHidden(false)}
                                title='إظهار قائمة الفصول'
                            >
                                <PanelIcon collapsed /> الفصول
                            </button>
                        )}
                        {book && (
                            <button
                                type='button'
                                className={'read-toolbar-bookmark' + (book.is_bookmarked ? ' is-active' : '')}
                                onClick={toggleBookmark}
                                disabled={bookmarking}
                                aria-pressed={book.is_bookmarked}
                                title={book.is_bookmarked ? 'إزالة من العلامات' : 'إضافة إلى العلامات'}
                            >
                                {book.is_bookmarked ? '★ محفوظ' : '☆ احفظ'}
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <p className='read-empty'>جارٍ التحميل…</p>
                ) : error || !book ? (
                    <p className='read-empty'>{error ?? 'الكتاب غير موجود'}</p>
                ) : (
                    // Collapsed chapters drop out of the grid entirely, so the
                    // article is as wide as in a book with no chapter list.
                    <div className={'read-layout' + (chapters.length > 1 && !chaptersHidden ? ' has-toc' : '')}>
                        {chapters.length > 1 && !chaptersHidden && (
                            <aside className='read-toc'>
                                <div className='read-toc-header'>
                                    <span>الفصول</span>
                                    <button
                                        type='button'
                                        className='read-toc-toggle'
                                        onClick={() => setChaptersHidden(true)}
                                        aria-label='إخفاء قائمة الفصول'
                                        title='إخفاء'
                                    >
                                        <PanelIcon />
                                    </button>
                                </div>
                                {chapterList(setActiveIdx)}
                            </aside>
                        )}

                        <article className='read-article'>
                            <header className='read-header'>
                                {book.cover_image && (
                                    <img
                                        className='read-cover'
                                        src={absoluteUrl(book.cover_image)}
                                        alt={`غلاف ${book.title}`}
                                    />
                                )}
                                <div className='read-header-text'>
                                    <h1 className='read-title'>{book.title}</h1>
                                    <p className='read-author'>
                                        بقلم {book.author && !book.is_anonymous
                                            ? <Link to={`/author/${encodeURIComponent(book.author)}`} className='read-author-link'>{book.display_author || book.author}</Link>
                                            : (book.display_author || book.author)}
                                    </p>
                                    {book.category_label && (
                                        <span className='read-category-tag'>{book.category_label}</span>
                                    )}
                                    {book.published_at && (
                                        <p className='read-dates'>
                                            نُشر في {formatDate(book.published_at)}
                                            {!sameDay(book.published_at, book.date_modified) && (
                                                <> · آخر تحديث {formatDate(book.date_modified)}</>
                                            )}
                                        </p>
                                    )}

                                    <div className='read-stats'>
                                        <span className='read-views'>
                                            {(book.view_count ?? 0).toLocaleString('ar-EG')} مشاهدة
                                        </span>
                                        <span className='read-rating-summary'>
                                            <Stars value={Math.round(book.avg_rating ?? 0)} size={18} />
                                            {book.avg_rating
                                                ? <span>{book.avg_rating} ({book.rating_count})</span>
                                                : <span>لا تقييمات بعد</span>}
                                        </span>
                                    </div>

                                    {book.description && (
                                        <p className='read-description'>{book.description}</p>
                                    )}
                                </div>
                            </header>

                            {chapters.length === 0 ? (
                                <p className='read-empty'>هذا الكتاب لا يحتوي على فصول بعد.</p>
                            ) : (
                                <>
                                    {activeChapter?.title && (
                                        <h2 className='read-chapter-title'>{activeChapter.title}</h2>
                                    )}
                                    {user && (
                                        <p className='read-hl-hint'>ظلّل أي نص لحفظه في تظليلاتك.</p>
                                    )}
                                    <div
                                        ref={contentRef}
                                        className='read-content read-content--rich'
                                        onClick={onContentClick}
                                        dangerouslySetInnerHTML={{ __html: activeHtml }}
                                    />

                                    <div className='read-chapter-bookmark'>
                                        <button
                                            className={chapterBookmarked ? 'is-active' : ''}
                                            onClick={toggleChapterBookmark}
                                            disabled={bookmarking}
                                            aria-pressed={chapterBookmarked}
                                        >
                                            {chapterBookmarked ? '★ علامة محفوظة عند هذا الفصل — إزالة' : '☆ احفظ علامة عند هذا الفصل'}
                                        </button>
                                    </div>

                                    {chapters.length > 1 && (
                                        <nav className='read-chapter-nav' aria-label='التنقل بين الفصول'>
                                            <button
                                                disabled={activeIdx === 0}
                                                onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
                                            >
                                                الفصل السابق
                                            </button>
                                            <span className='read-chapter-count'>
                                                {activeIdx + 1} / {chapters.length}
                                            </span>
                                            <button
                                                disabled={activeIdx === chapters.length - 1}
                                                onClick={() => setActiveIdx(i => Math.min(chapters.length - 1, i + 1))}
                                            >
                                                الفصل التالي
                                            </button>
                                        </nav>
                                    )}
                                </>
                            )}

                            {user && highlights.length > 0 && (
                                <section className='read-highlights'>
                                    <h3>تظليلاتي في هذا الكتاب <small>({highlights.length})</small></h3>
                                    <ul>
                                        {highlights.map(h => (
                                            <li key={h.id}>
                                                <button type='button' className='read-highlight-text' onClick={() => goToHighlight(h)}>
                                                    <span className='read-highlight-chapter'>
                                                        {h.chapter_title || `الفصل ${(h.chapter_order ?? 0) + 1}`}
                                                    </span>
                                                    <q>{h.text}</q>
                                                </button>
                                                <button
                                                    type='button'
                                                    className='read-highlight-remove'
                                                    onClick={() => removeHighlight(h.id)}
                                                    aria-label='حذف التظليل'
                                                    title='حذف'
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {!isAuthor && (
                            <section className='read-rating-section'>
                                <h3>قيّم هذا الكتاب</h3>
                                <Stars
                                    interactive
                                    value={book.my_rating ?? 0}
                                    onSelect={rate}
                                />
                                {!user && (
                                    <p className='read-rating-login'>
                                        <button type='button' className='read-rating-remove' onClick={() => setAccountPrompt(true)}>
                                            أنشئ حسابًا أو سجّل الدخول
                                        </button>
                                        {' '}لتقييم الكتاب
                                    </p>
                                )}
                                {book.my_rating && (
                                    <div className='read-my-rating'>
                                        <small>تقييمك الحالي: {book.my_rating} / 5</small>
                                        <button
                                            type='button'
                                            className='read-rating-remove'
                                            onClick={removeRating}
                                        >
                                            احذف تقييمي
                                        </button>
                                    </div>
                                )}
                            </section>
                            )}
                        </article>
                    </div>
                )}
            </div>

            {popover && (
                <div
                    className='read-hl-popover'
                    style={{ left: popover.x, top: popover.y }}
                    // keep the selection: a mousedown here would otherwise collapse it
                    onMouseDown={e => e.preventDefault()}
                >
                    {popover.pending ? (
                        <button type='button' onClick={saveHighlight}>✎ تظليل</button>
                    ) : (
                        <button type='button' className='is-danger' onClick={() => removeHighlight(popover.id)}>
                            × حذف التظليل
                        </button>
                    )}
                    <button type='button' className='read-hl-popover-close' onClick={() => setPopover(null)} aria-label='إغلاق'>
                        ×
                    </button>
                </div>
            )}

            {chapters.length > 1 && (
                <>
                    <button
                        type='button'
                        className='read-chapter-tab'
                        onClick={() => setDrawerOpen(true)}
                        aria-label='إظهار قائمة الفصول'
                    >
                        الفصول
                    </button>
                    {drawerOpen && (
                        <div className='read-drawer-overlay' onClick={() => setDrawerOpen(false)} />
                    )}
                    <aside className={'read-drawer' + (drawerOpen ? ' is-open' : '')} aria-hidden={!drawerOpen}>
                        <div className='read-drawer-header'>
                            <span>الفصول</span>
                            <button
                                type='button'
                                className='read-drawer-close'
                                onClick={() => setDrawerOpen(false)}
                                aria-label='إغلاق'
                            >
                                ×
                            </button>
                        </div>
                        {chapterList(pickFromDrawer)}
                    </aside>
                </>
            )}
        </main>
        </>
    );
}
